import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

// Floating particle snow effect
function ParticleSnow({ count = 40 }) {
  const particles = useMemo(() => Array.from({ length: count }, (_, i) => {
    const isOrange = Math.random() > 0.5;
    return {
      id: i,
      left: Math.random() * 100,
      size: 2 + Math.random() * 4,
      duration: 8 + Math.random() * 12,
      delay: Math.random() * 10,
      opacity: 0.15 + Math.random() * 0.25,
      drift: -20 + Math.random() * 40,
      color: isOrange ? '#FF9A00' : '#ffffff',
    };
  }), [count]);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 1 }}>
      <style>{`
        @keyframes snowfall {
          0% { transform: translateY(-10px) translateX(0px); opacity: 0; }
          10% { opacity: var(--p-opacity); }
          90% { opacity: var(--p-opacity); }
          100% { transform: translateY(calc(100vh + 10px)) translateX(var(--p-drift)); opacity: 0; }
        }
      `}</style>
      {particles.map(p => (
        <div
          key={p.id}
          style={{
            position: 'absolute',
            left: `${p.left}%`,
            top: '-10px',
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            backgroundColor: p.color,
            opacity: 0,
            animation: `snowfall ${p.duration}s ${p.delay}s linear infinite`,
            '--p-opacity': p.opacity,
            '--p-drift': `${p.drift}px`,
          }}
        />
      ))}
    </div>
  );
}
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Eye, MousePointerClick, Smartphone, Monitor, Clock, BarChart3, Flame, Lock, Loader2, LogOut, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BUTTONS_MAP = {
  'sticky-signup': 'Sign Up Free (Sticky)',
  'hero-start-selling': 'Start Selling Free (Hero)',
  'pricing-free': 'Start Free (Free Plan)',
  'pricing-pro': 'Start Pro Trial (Pro Plan)',
  'pricing-premium': 'Start Premium (Premium Plan)',
  'cta-create-store': 'Create Your Store Now (CTA)',
};

const SECTION_NAMES = {
  hero: 'Hero Section',
  'how-it-works': 'How It Works',
  features: 'Features',
  pricing: 'Pricing',
  testimonials: 'Testimonials',
  faq: 'FAQ',
  'final-cta': 'Final CTA',
};

function AuthScreen({ onAuth }) {
  const [step, setStep] = useState(0); // 0=username, 1=combo
  const [username, setUsername] = useState('');
  const [combo, setCombo] = useState(['', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const numbers = Array.from({ length: 101 }, (_, i) => String(i).padStart(2, '0'));

  const handleUsername = () => {
    if (!username.trim()) { setError('Enter username'); return; }
    setError('');
    setStep(1);
  };

  const handleCombo = async () => {
    if (combo.some(c => c === '')) { setError('Select all 3 numbers'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/lp/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, combo }),
      });
      if (!res.ok) {
        const d = await res.json();
        setError(d.detail || 'Invalid credentials');
        setLoading(false);
        return;
      }
      const data = await res.json();
      sessionStorage.setItem('lp_token', data.token);
      onAuth(data.token);
    } catch {
      setError('Connection failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#195259' }}>
      <ParticleSnow count={35} />
      <div className="w-full max-w-[420px] relative z-10 mx-4">
        {/* Glow effect behind card */}
        <div className="absolute -inset-4 rounded-[32px] opacity-20 blur-2xl" style={{ background: 'radial-gradient(circle at 50% 0%, #FF9A00 0%, transparent 70%)' }} />
        
        <div className="relative rounded-[24px] overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.12)' }}>
          {/* Orange accent bar top */}
          <div className="h-1.5" style={{ background: 'linear-gradient(90deg, #FF9A00, #FFB84D, #FF9A00)' }} />
          
          <div className="px-8 pt-8 pb-9">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img src="/logos/funnely-white.png" alt="Funnely" className="h-8 object-contain" />
            </div>

            {/* Lock icon with orange ring */}
            <div className="flex justify-center mb-5">
              <div className="h-16 w-16 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255,154,0,0.15)', border: '2px solid rgba(255,154,0,0.3)' }}>
                <Lock className="h-7 w-7" style={{ color: '#FF9A00' }} />
              </div>
            </div>

            <h2 className="text-xl font-bold text-center mb-1 text-white">Secure Access</h2>
            <p className="text-xs text-center mb-7" style={{ color: 'rgba(255,255,255,0.4)' }}>Landing Page Analytics Dashboard</p>

            {error && (
              <div className="mb-5 p-3 rounded-xl text-xs text-center font-medium" style={{ backgroundColor: 'rgba(239,68,68,0.15)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.2)' }}>
                {error}
              </div>
            )}

            {step === 0 ? (
              <div className="space-y-4">
                <div className="relative">
                  <Input
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    placeholder="Enter username"
                    onKeyDown={e => e.key === 'Enter' && handleUsername()}
                    className="h-12 bg-white/10 border-white/10 text-white placeholder:text-white/30 focus:border-[#FF9A00]/50 focus:ring-[#FF9A00]/20"
                    data-testid="lp-username"
                  />
                </div>
                <Button className="w-full h-12 font-bold text-sm rounded-xl shadow-lg" style={{ backgroundColor: '#FF9A00', color: '#195259', boxShadow: '0 4px 20px rgba(255,154,0,0.3)' }} onClick={handleUsername}>
                  Continue
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="text-center">
                  <p className="text-xs font-medium mb-1" style={{ color: '#FF9A00' }}>Step 2 of 2</p>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Select your 3-number combination</p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[0, 1, 2].map(i => (
                    <Select key={i} value={combo[i]} onValueChange={v => setCombo(prev => { const n = [...prev]; n[i] = v; return n; })}>
                      <SelectTrigger className="h-14 text-center font-mono text-xl font-bold bg-white/10 border-white/10 text-white" data-testid={`lp-combo-${i}`}>
                        <SelectValue placeholder="--" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px]">
                        {numbers.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ))}
                </div>
                <Button className="w-full h-12 font-bold text-sm rounded-xl shadow-lg" style={{ backgroundColor: '#FF9A00', color: '#195259', boxShadow: '0 4px 20px rgba(255,154,0,0.3)' }} onClick={handleCombo} disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
                  Unlock Dashboard
                </Button>
                <button onClick={() => { setStep(0); setError(''); }} className="text-xs hover:underline w-full text-center" style={{ color: 'rgba(255,255,255,0.35)' }}>Back to username</button>
              </div>
            )}
          </div>

          {/* Bottom decorative accent */}
          <div className="h-1" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,154,0,0.3), transparent)' }} />
        </div>
      </div>
    </div>
  );
}

function HeatmapBar({ section, totalTime, maxTime }) {
  const pct = maxTime > 0 ? (totalTime / maxTime) * 100 : 0;
  const intensity = Math.min(pct / 100, 1);
  const r = Math.round(25 + (255 - 25) * intensity);
  const g = Math.round(82 + (154 - 82) * intensity);
  const b = Math.round(89 - 89 * intensity);
  const color = `rgb(${r},${g},${b})`;

  return (
    <div className="flex items-center gap-3">
      <span className="text-xs w-28 shrink-0 truncate" style={{ color: '#ffffff' }}>{SECTION_NAMES[section] || section}</span>
      <div className="flex-1 h-7 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
        <div className="h-full rounded-full transition-all duration-700 flex items-center px-3" style={{ width: `${Math.max(pct, 8)}%`, backgroundColor: color }}>
          <span className="text-[10px] font-bold text-white">{Math.round(totalTime)}s</span>
        </div>
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (period === 'custom' && dateFrom && dateTo) {
        params.set('date_from', dateFrom);
        params.set('date_to', dateTo);
      }
      const res = await fetch(`${API}/api/lp/dashboard?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { onLogout(); return; }
      const d = await res.json();
      setData(d);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [token, period, dateFrom, dateTo, onLogout]);

  useEffect(() => { loadData(); }, [loadData]);

  const maxHeatTime = data?.heatmap?.length ? Math.max(...data.heatmap.map(h => h.total_time)) : 0;
  const maxHourly = data?.hourly_traffic?.length ? Math.max(...data.hourly_traffic.map(h => h.count)) : 0;
  const totalClicks = data?.button_clicks ? Object.values(data.button_clicks).reduce((a, b) => a + b, 0) : 0;
  const totalDevices = data?.devices ? Object.values(data.devices).reduce((a, b) => a + b, 0) : 0;
  const totalOS = data?.os_data ? Object.values(data.os_data).reduce((a, b) => a + b, 0) : 0;

  const formatDuration = (s) => {
    if (s < 60) return `${Math.round(s)}s`;
    return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#195259' }}>
      <ParticleSnow count={50} />

      <div className="relative z-10 max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Landing Page Dashboard</h1>
            <p className="text-sm mt-0.5" style={{ color: 'rgba(255,255,255,0.5)' }}>Marketing performance analytics</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={v => setPeriod(v)}>
              <SelectTrigger className="w-[180px] h-10 text-white border-white/20" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="7days">7 Days</SelectItem>
                <SelectItem value="14days">14 Days</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="last_month">Last Month</SelectItem>
                <SelectItem value="maximum">Maximum</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" className="h-10 w-10 text-white hover:bg-white/10" onClick={onLogout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {period === 'custom' && (
          <div className="flex gap-3 mb-6">
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-44 text-white border-white/20" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-44 text-white border-white/20" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
            <Button size="sm" onClick={loadData} style={{ backgroundColor: '#195259' }} className="text-white">Apply</Button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin" style={{ color: '#ffffff' }} /></div>
        ) : !data ? (
          <p className="text-center text-white/50 py-20">No data available</p>
        ) : (
          <div className="space-y-6">
            {/* KPI Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Page Views', value: data.total_views, icon: Eye, accent: false },
                { label: 'Unique Visitors', value: data.unique_sessions, icon: Eye, accent: true },
                { label: 'Total Clicks', value: totalClicks, icon: MousePointerClick, accent: false },
                { label: 'Avg. Time on Page', value: formatDuration(data.avg_duration), icon: Clock, accent: true },
              ].map((kpi, i) => (
                <div key={i} className="rounded-2xl overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="h-1" style={{ backgroundColor: kpi.accent ? '#FF9A00' : 'rgba(255,255,255,0.15)' }} />
                  <div className="pt-4 pb-4 px-5">
                    <div className="flex items-center gap-2 mb-2">
                      <kpi.icon className="h-4 w-4" style={{ color: kpi.accent ? '#FF9A00' : 'rgba(255,255,255,0.6)' }} />
                      <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{kpi.label}</span>
                    </div>
                    <p className="text-2xl font-bold text-white">{typeof kpi.value === 'number' ? kpi.value.toLocaleString() : kpi.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Button Clicks */}
            <Card className="rounded-2xl overflow-hidden" style={{ backgroundColor: "rgba(255,255,255,0.08)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.08)" }}>
              <CardContent className="pt-5">
                <div className="flex items-center gap-2 mb-4">
                  <MousePointerClick className="h-4 w-4" style={{ color: '#FF9A00' }} />
                  <h3 className="text-sm font-semibold" style={{ color: '#ffffff' }}>CTA Button Clicks</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(BUTTONS_MAP).map(([key, label]) => (
                    <div key={key} className="flex items-center justify-between p-3 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
                      <span className="text-xs truncate mr-2" style={{ color: '#ffffff' }}>{label}</span>
                      <Badge className="shrink-0 font-bold text-sm" style={{ backgroundColor: (data.button_clicks?.[key] || 0) > 0 ? '#FF9A00' : '#e5e5e5', color: (data.button_clicks?.[key] || 0) > 0 ? '#195259' : '#999' }}>
                        {data.button_clicks?.[key] || 0}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Device & OS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="rounded-2xl overflow-hidden" style={{ backgroundColor: "rgba(255,255,255,0.08)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <CardContent className="pt-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Smartphone className="h-4 w-4" style={{ color: '#ffffff' }} />
                    <h3 className="text-sm font-semibold" style={{ color: '#ffffff' }}>Device Type</h3>
                  </div>
                  {['Mobile', 'Desktop'].map(d => {
                    const count = data.devices?.[d] || 0;
                    const pct = totalDevices > 0 ? (count / totalDevices) * 100 : 0;
                    return (
                      <div key={d} className="mb-3">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="flex items-center gap-2">
                            {d === 'Mobile' ? <Smartphone className="h-3.5 w-3.5" /> : <Monitor className="h-3.5 w-3.5" />}
                            <span style={{ color: '#ffffff' }}>{d}</span>
                          </span>
                          <span className="font-semibold" style={{ color: '#ffffff' }}>{count} <span className="text-xs font-normal text-white/50">({Math.round(pct)}%)</span></span>
                        </div>
                        <div className="h-2.5 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: d === 'Mobile' ? '#FF9A00' : '#195259' }} />
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              <Card className="rounded-2xl overflow-hidden" style={{ backgroundColor: "rgba(255,255,255,0.08)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <CardContent className="pt-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Monitor className="h-4 w-4" style={{ color: '#FF9A00' }} />
                    <h3 className="text-sm font-semibold" style={{ color: '#ffffff' }}>Operating System</h3>
                  </div>
                  {['iOS', 'Android', 'macOS', 'Windows'].map((os, i) => {
                    const count = data.os_data?.[os] || 0;
                    const pct = totalOS > 0 ? (count / totalOS) * 100 : 0;
                    const colors = ['#FF9A00', '#195259', '#2B7A84', '#6D6E71'];
                    return (
                      <div key={os} className="mb-3">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span style={{ color: '#ffffff' }}>{os}</span>
                          <span className="font-semibold" style={{ color: '#ffffff' }}>{count} <span className="text-xs font-normal text-white/50">({Math.round(pct)}%)</span></span>
                        </div>
                        <div className="h-2.5 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: colors[i] }} />
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>

            {/* Heatmap */}
            <Card className="rounded-2xl overflow-hidden" style={{ backgroundColor: "rgba(255,255,255,0.08)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.08)" }}>
              <CardContent className="pt-5">
                <div className="flex items-center gap-2 mb-4">
                  <Flame className="h-4 w-4" style={{ color: '#FF9A00' }} />
                  <h3 className="text-sm font-semibold" style={{ color: '#ffffff' }}>Section Heatmap</h3>
                  <span className="text-[10px] text-white/50 ml-auto">Time spent per section</span>
                </div>
                <div className="space-y-3">
                  {data.heatmap?.length > 0 ? data.heatmap.map(h => (
                    <HeatmapBar key={h.section} section={h.section} totalTime={h.total_time} maxTime={maxHeatTime} />
                  )) : (
                    <p className="text-sm text-white/50 text-center py-4">No section data yet</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Hourly Traffic */}
            <Card className="rounded-2xl overflow-hidden" style={{ backgroundColor: "rgba(255,255,255,0.08)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.08)" }}>
              <CardContent className="pt-5">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 className="h-4 w-4" style={{ color: '#ffffff' }} />
                  <h3 className="text-sm font-semibold" style={{ color: '#ffffff' }}>Hourly Traffic</h3>
                  <span className="text-[10px] text-white/50 ml-auto">Malaysia Time (UTC+8)</span>
                </div>
                <div className="flex items-end gap-1 h-48">
                  {data.hourly_traffic?.map((h, i) => {
                    const myt = (h.hour + 8) % 24;
                    const barH = maxHourly > 0 ? (h.count / maxHourly) * 100 : 0;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <span className="text-[9px] font-bold" style={{ color: h.count > 0 ? '#195259' : 'transparent' }}>{h.count}</span>
                        <div className="w-full rounded-t-sm transition-all" style={{ height: `${Math.max(barH, 2)}%`, backgroundColor: h.count > 0 ? '#195259' : '#e8e8e8' }} />
                        <span className="text-[8px] text-white/50">{String(myt).padStart(2, '0')}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

export default function LPDashboardPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem('lp_token') || '');

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem('lp_token');
    setToken('');
  }, []);

  if (!token) return <AuthScreen onAuth={setToken} />;
  return <Dashboard token={token} onLogout={handleLogout} />;
}
